import { useEffect, type PropsWithChildren } from 'react';
import { XIcon } from './Icons';

interface ModalProps extends PropsWithChildren {
  open: boolean;
  title: string;
  eyebrow?: string;
  onClose: () => void;
  size?: 'medium' | 'large';
  variant?: 'standard' | 'mobile-page';
  bodyScrollable?: boolean;
}

export function Modal({
  open,
  title,
  eyebrow,
  onClose,
  size = 'medium',
  variant = 'standard',
  bodyScrollable = false,
  children,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    const root = document.documentElement;
    const previousViewportHeight = root.style.getPropertyValue('--app-visual-viewport-height');
    const previousViewportTop = root.style.getPropertyValue('--app-visual-viewport-offset-top');
    const hadKeyboardClass = root.classList.contains('is-virtual-keyboard-open');
    const viewport = window.visualViewport;
    const initialViewportHeight = viewport?.height ?? window.innerHeight;
    const syncVisualViewport = () => {
      root.style.setProperty('--app-visual-viewport-height', `${Math.round(viewport?.height ?? window.innerHeight)}px`);
      root.style.setProperty('--app-visual-viewport-offset-top', `${Math.round(viewport?.offsetTop ?? 0)}px`);
      root.classList.toggle('is-virtual-keyboard-open', (viewport?.height ?? window.innerHeight) < initialViewportHeight - 120);
    };
    document.body.style.overflow = 'hidden';
    syncVisualViewport();
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', syncVisualViewport);
    viewport?.addEventListener('resize', syncVisualViewport);
    viewport?.addEventListener('scroll', syncVisualViewport);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', syncVisualViewport);
      viewport?.removeEventListener('resize', syncVisualViewport);
      viewport?.removeEventListener('scroll', syncVisualViewport);
      if (previousViewportHeight) root.style.setProperty('--app-visual-viewport-height', previousViewportHeight);
      else root.style.removeProperty('--app-visual-viewport-height');
      if (previousViewportTop) root.style.setProperty('--app-visual-viewport-offset-top', previousViewportTop);
      else root.style.removeProperty('--app-visual-viewport-offset-top');
      root.classList.toggle('is-virtual-keyboard-open', hadKeyboardClass);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={`modal modal--${size}${variant === 'mobile-page' ? ' modal--mobile-page' : ''}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <header className="modal__header">
          <div>
            {eyebrow && <p className="eyebrow">{eyebrow}</p>}
            <h2 id="modal-title">{title}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="关闭">
            <XIcon />
          </button>
        </header>
        <div className={`modal__body${bodyScrollable ? ' modal__body--scrollable' : ''}`}>{children}</div>
      </section>
    </div>
  );
}
